/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react'
import { Modal, Drawer, Spin, Table } from 'antd'
import PropTypes from 'prop-types'
import { observer } from 'mobx-react-lite'
import { runInAction, toJS } from 'mobx'
import { FormatedTimestamp } from '../../Shared/FormatedTimestamp'
import { DATE_TIME_FORMAT, DO_LOAD } from '../../../config'
import { useStores } from '../../../cls/stores/MobXProvider'
import { SubtasksList } from './SubtasksList'
import { UserString } from '../../Shared'
import SimpleProgressView from './SimpleProgressView'
import { useSmartTranslation } from '../../../cls/TranslationContext'

// Helper to calculate subtasks history and status
/**
 * generateSubtasksStatus computes counts of subtasks by status category.
 *
 * @function
 * @param {Array<Object>} subtasks – Array of subtask objects, each with a `status` property.
 * @returns {Object} Object with keys `active`, `pending`, and `completed` and their respective counts.
 */
const generateSubtasksStatus = (subtasks) => {
	return subtasks.reduce(
		(acc, { status }) => {
			if (status === 'completed' || status === 'canceled') {
				acc.completed++
			} else if (
				status === 'pending' ||
				status === 'draft' ||
				status === 'in_review'
			) {
				acc.pending++
			} else {
				acc.active++
			}
			return acc
		},
		{ active: 0, pending: 0, completed: 0 }
	)
}

/**
 * defineProgressBasedOnSubtasks derives an overall progress state from a list of subtasks.
 *
 * @function
 * @param {Array<Object>} subtasks – Array of subtask objects.
 * @returns {'active'|'pending'|'completed'} One of the three progress states.
 */
const defineProgressBasedOnSubtasks = (subtasks) => {
	const { active, pending, completed } = generateSubtasksStatus(subtasks)
	const total = subtasks.length
	if (completed === subtasks.length) {
		return 'completed'
	} else if (pending > 0 || completed > 0 || active < total) {
		return 'pending'
	} else {
		return 'active'
	}
}

// Function to generate full history of a specific subtask
/**
 * generateSubtaskFullHistory builds the full history entries for a given subtask.
 * It chooses between recurring vs. one-off history methods and attaches the subtask’s displayName.
 *
 * @function
 * @param {Object} _itm – The parent item (e.g. sprint) instance with `history` and `subtasks` stores.
 * @param {string} historyCategory – Category key under which the history is recorded (e.g. 'subtasks').
 * @param {string} subtaskID – The ID of the subtask to retrieve history for.
 * @returns {Array<Object>} Array of history entries augmented with `name: subtask.displayName`.
 */
const generateSubtaskFullHistory = (_itm, historyCategory, subtaskID) => {
	const subtask = _itm.subtasks.getSubtaskById(subtaskID)
	let subtaskHistory = []
	// If recurence we use history.getFullHistoryItem
	if (_itm.recurrence.type && _itm.recurrence.type !== '') {
		subtaskHistory = _itm.history.getFullHistoryItem(
			historyCategory,
			subtaskID,
			false
		)
	} else {
		subtaskHistory = _itm.history.getHistoryItem(historyCategory, subtaskID)
	}

	// Add subtask name to the history
	subtaskHistory = subtaskHistory.map((item) => {
		return {
			...item,
			name: subtask.displayName
		}
	})

	return subtaskHistory
}

// Loading spinner subcomponent
/**
 * LoadingSpinner component renders a centered spinner with a localized "Loading" label.
 *
 * @component
 * @param {Object} props.t – Translation function from `useSmartTranslation()`.
 * @returns {JSX.Element}
 */
const LoadingSpinner = ({ t }) => (
	<div style={{ textAlign: 'center' }}>
		<p>{t('single.loading')}</p>
		<Spin />
	</div>
)

// History Drawer subcomponent
/**
 * HistoryDrawer component displays a Drawer with a table of history entries.
 * It auto-detects which columns to show based on the presence of data fields.
 *
 * @component
 * @param {boolean} props.historyIsOpen – Whether the drawer is visible.
 * @param {Object} props.historyValue – The history payload with nested arrays or objects.
 * @param {Function} props.onClose – Callback to close the drawer.
 * @param {boolean} props.isSuperAdmin – If true, shows full timestamp details.
 * @param {Function} props.t – Translation function from `useSmartTranslation()`.
 * @returns {JSX.Element|null}
 */
const HistoryDrawer = observer(
	({ historyIsOpen, historyValue = {}, onClose, isSuperAdmin, t }) => {
		if (!historyIsOpen) return null

		let datasource = []
		// Check if historyValue.history is an object or array
		if (
			Array.isArray(historyValue.history) ||
			Array.isArray(Object.values(historyValue.history)[0])
		) {
			datasource = Object.values(toJS(historyValue.history)).flat()
		} else {
			datasource = toJS(historyValue.history)
		}
		const hasData = datasource.length > 0
		// Order datasource by createdTs DESC
		hasData && datasource.sort((a, b) => b.createdTs - a.createdTs)

		// return null

		const activeForRecurenceDate =
			hasData &&
			datasource.every((item) => {
				return (
					item.hasOwnProperty('forRecurenceDate') &&
					item.forRecurenceDate
				)
			})
		const activeForSubtaskId =
			hasData &&
			datasource.every((item) => {
				return (
					item.hasOwnProperty('forSubtaskId') &&
					item.forSubtaskId !== ''
				)
			})
		// All forSubtaskId are the same
		const sameSubtaskId =
			activeForSubtaskId &&
			hasData &&
			datasource.every(
				(val, i, arr) => val.forSubtaskId === arr[0].forSubtaskId
			)

		const activeValue =
			hasData &&
			datasource.every((item) => {
				return item.hasOwnProperty('value') && item.value
			})
		const activeCreatedTs =
			hasData &&
			datasource.every((item) => {
				return item.hasOwnProperty('createdTs') && item.createdTs
			})
		const activeCreatedBy =
			hasData &&
			datasource.every((item) => {
				return item.hasOwnProperty('createdBy') && item.createdBy
			})

		const countActiveColumns = [
			activeForRecurenceDate,
			activeForSubtaskId,
			activeValue,
			activeCreatedTs,
			activeCreatedBy
		].filter((item) => item).length

		const historyColumns = []
		if (activeForRecurenceDate) {
			historyColumns.push({
				title: t('_recurrence'),
				dataIndex: 'forRecurenceDate',
				key: 'forRecurenceDate',
				render: (forRecurenceDate) => (
					<FormatedTimestamp timestamp={forRecurenceDate} />
				)
			})
		}
		if (activeForSubtaskId && false) {
			historyColumns.push({
				title: t('single.subtask'),
				dataIndex: 'forSubtaskId',
				key: 'forSubtaskId',
				render: (forSubtaskId) => <p>{forSubtaskId}</p>
			})
		}
		if (activeValue) {
			historyColumns.push({
				title: t('single.status'),
				dataIndex: 'value',
				key: 'value'
			})
		}
		if (activeCreatedTs) {
			historyColumns.push({
				title: t('single.date'),
				dataIndex: 'createdTs',
				key: 'createdTs',
				render: (createdTs) => (
					<FormatedTimestamp
						timestamp={createdTs}
						formatedAs={isSuperAdmin ? DATE_TIME_FORMAT : null}
					/>
				)
			})
		}
		if (activeCreatedBy) {
			historyColumns.push({
				title: t('single.action-by'),
				dataIndex: 'createdBy',
				key: 'createdBy',
				render: (createdBy) => (
					<UserString userId={createdBy} path="name" />
				)
			})
		}

		return (
			<Drawer
				size={countActiveColumns > 3 ? 'large' : 'default'}
				title={t('single.history')}
				onClose={onClose}
				open={historyIsOpen}
			>
				{sameSubtaskId && datasource[0]?.name ? (
					<p>Subtask: {datasource[0].name}</p>
				) : (
					<p>{historyValue?.name || t('single.no-history')}</p>
				)}
				<Table
					dataSource={
						hasData
							? datasource.map((item, index) => ({
									...item,
									key: item?.key || index
							  }))
							: []
					}
					columns={historyColumns}
				/>
			</Drawer>
		)
	}
)

// ProgressModal main component
/**
 * ProgressModal component shows a modal to adjust and view progress or subtask statuses,
 * including a HistoryDrawer for detailed audit logs.
 *
 * @component
 * @param {string} props.modalKey – Key to read/write visibility/item from `uiStore.modals`.
 * @param {string} [props.historyKey='history-drawer'] – Key for the history drawer modal.
 * @param {Object} props.uiStore – UI store containing modal state.
 * @param {Function} props.requireItemById – Function to fetch the target item (e.g. sprint) by ID.
 * @param {Function} props.loadItm – Async function to load item data if needed.
 * @param {Function} props.saveItem – Function to persist changes to the item.
 * @param {string} [props.type='sprint'] – Type label for logging and translations.
 * @returns {JSX.Element}
 */
const ProgressModal = observer(
	({
		modalKey,
		historyKey = 'history-drawer',
		uiStore,
		requireItemById,
		loadItm,
		saveItem,
		type = 'sprint',
		resetValue = 'active'
	}) => {
		const { t } = useSmartTranslation()
		const { users } = useStores()
		const { loggedInUserId, loggedInUser } = users
		const isSuperAdmin = loggedInUser.isSuperAdmin
		const [loadingItm, setLoadingItm] = useState(true)

		const { visible, itmId } = uiStore.modals[modalKey] || {
			visible: false,
			itmId: null
		}
		const _itm = itmId ? requireItemById(itmId) : null

		const hasSubtasks = _itm?.subtasks?.list.length > 0
		const _recurrence = _itm?.recurrence || {}
		const hasRecurrence =
			(_itm?.recurrence?.type && _itm?.recurrence?.type !== '') || false
		const recurrenceNext = _itm?.recurrence?.next || ''

		const keySubtasks = hasRecurrence ? 'recurrence_subtasks' : 'subtasks'
		const keyProgress = hasRecurrence ? 'recurrence' : 'progress'

		const historyIsOpen = uiStore.modals[historyKey]?.visible
		const historyValue = uiStore.modals[historyKey]?.item

		useEffect(() => {
			if (itmId) {
				if (_itm.loaded === DO_LOAD) {
					loadItm(itmId).finally(() => setLoadingItm(false))
				} else {
					setLoadingItm(false)
				}
			}
		}, [itmId])

		/**
		 * handleResetProgress resets the item’s progress and history,
		 * resets all subtasks and/or recurrence to active, then persists.
		 *
		 * @function
		 * @private
		 */
		const handleResetProgress = () => {
			try {
				console.debug('Resetting progress...')
				runInAction(() => {
					_itm.history.resetHistory()
					_itm.setProgress(resetValue)
					_itm.chgInFull(['history'], _itm.history.categories)

					if (hasSubtasks) {
						_itm.subtasks.list.forEach((subtask) =>
							subtask.setStatus(resetValue)
						)
						_itm.chgInFull(['subtasks'], toJS(_itm.subtasks.list))
					}

					if (hasRecurrence) {
						_itm.recurrence.resetRecurrence()
						_itm.setRecurrenceNext()
						_itm.chgInFull(['recurrence'], toJS(_itm.recurrence))
					}
				})
				saveItem(_itm.id, toJS(_itm.full))
			} catch (err) {
				console.error('Error resetting progress:', err)
			}
		}

		/**
		 * handleSubtaskProgress updates a single subtask’s status, records history,
		 * recalculates overall progress or marks recurrence completed, then persists.
		 *
		 * @function
		 * @private
		 * @param {string} subtaskId – ID of the subtask to update.
		 * @param {string} value – New status value ('draft','pending','completed', etc.).
		 */
		const handleSubtaskProgress = (subtaskId, value) => {
			try {
				console.debug(
					`Updating subtask [${subtaskId}] with value [${value}]...`
				)
				runInAction(() => {
					const subtask = _itm.subtasks.getSubtaskById(subtaskId)
					subtask.setStatus(value)

					_itm.history.addHistory(
						keySubtasks,
						hasRecurrence
							? `${recurrenceNext}_${subtaskId}`
							: subtaskId,
						{
							forSubtaskId: subtaskId,
							forRecurenceDate: recurrenceNext,
							value,
							createdBy: loggedInUserId
						}
					)

					const allCompleted = _itm.subtasks.list.every(
						(st) => st.status === 'completed'
					)
					console.debug('All subtasks completed:', allCompleted)

					if (hasRecurrence && allCompleted) {
						console.debug(
							'Marking recurrence as completed...',
							_itm.recurrence.nextHistory
						)
						try {
							_itm.recurrence.completed(recurrenceNext)
							console.debug(
								'Recurrence marked as completed.',
								recurrenceNext
							)
						} catch (err) {
							console.error('Error completing recurrence:', err)
							return
						}

						try {
							_itm.setRecurrenceNext()
							console.debug(
								'Next recurrence date set:',
								_itm.recurrence.next
							)
						} catch (err) {
							console.error('Failed to set next recurrence:', err)
						}

						_itm.chgInFull(['recurrence'], toJS(_itm.recurrence))
					} else {
						const status = defineProgressBasedOnSubtasks(
							_itm.subtasks.list
						)
						_itm.setProgress(status)
					}

					_itm.chgInFull(['subtasks'], toJS(_itm.subtasks.list))
					_itm.chgInFull(['history'], _itm.history.categories)
				})
				saveItem(_itm.id, toJS(_itm.full))
			} catch (err) {
				console.error('Error in subtask progress handler:', err)
			}
		}

		/**
		 * handleSimpleProgress sets the item’s overall progress (no subtasks),
		 * optionally handles recurrence completion, records history, then persists.
		 *
		 * @function
		 * @private
		 * @param {string} value – New progress value.
		 */
		const handleSimpleProgress = (value) => {
			try {
				console.debug('Updating simple progress to:', value)
				runInAction(() => {
					_itm.setProgress(value)

					if (value === 'completed' && recurrenceNext !== '') {
						if (_recurrence.type !== 'deadline') {
							console.debug(
								'Completing and resetting recurrence...'
							)
							_itm.recurrence.completed(recurrenceNext)
							_itm.setRecurrenceNext()
							_itm.chgInFull(
								['recurrence'],
								toJS(_itm.recurrence)
							)
						}
					}

					_itm.history.addHistory(
						keyProgress,
						hasRecurrence ? recurrenceNext : _itm.id,
						{
							forRecurenceDate: recurrenceNext,
							value,
							createdBy: loggedInUserId
						}
					)

					_itm.chgInFull(['history'], _itm.history.categories)
				})
				saveItem(_itm.id, toJS(_itm.full))
			} catch (err) {
				console.error('Error in simple progress handler:', err)
			}
		}

		if (!_itm) return null

		return (
			<>
				<Modal
					width={800}
					keyboard
					open={visible}
					onCancel={() =>
						runInAction(() =>
							uiStore.setModal(modalKey, {
								visible: false,
								itmId: null
							})
						)
					}
					title={t('single.progress')}
					footer={false}
				>
					{loadingItm ? (
						<LoadingSpinner t={t} />
					) : (
						<>
							{_itm.progress === 'canceled' && !isSuperAdmin ? (
								<p>
									{t('__was-canceled%_sprint')}{' '}
									<i>({t('single.admin-ask-restore')})</i>
								</p>
							) : hasSubtasks ? (
								<SubtasksList
									historyKey={historyKey}
									_itm={_itm}
									category={keySubtasks}
									uiStore={uiStore}
									getHistory={(subtaskId) =>
										generateSubtaskFullHistory(
											_itm,
											keySubtasks,
											subtaskId
										)
									}
									dataSource={toJS(_itm.subtasks.list)}
									renderName={(id) =>
										_itm.subtasks.getSubtaskById(id)
											.displayName
									}
									_doSave={(subtaskId, value) => {
										if (value === 'reset-history') {
											handleResetProgress()
										} else {
											handleSubtaskProgress(
												subtaskId,
												value
											)
										}
									}}
								/>
							) : (
								<SimpleProgressView
									type={type}
									historyKey={historyKey}
									_itm={_itm}
									t={t}
									loggedInUserId={loggedInUserId}
									uiStore={uiStore}
									category={keyProgress}
									_doSave={(value) => {
										if (value === 'reset-history') {
											handleResetProgress()
										} else {
											handleSimpleProgress(value)
										}
									}}
								/>
							)}
						</>
					)}
				</Modal>

				<HistoryDrawer
					t={t}
					isSuperAdmin={isSuperAdmin}
					historyIsOpen={historyIsOpen}
					historyValue={historyValue}
					onClose={() =>
						runInAction(() => delete uiStore.modals[historyKey])
					}
				/>
			</>
		)
	}
)

ProgressModal.propTypes = {
	CustomClass: PropTypes.func,
	modalKey: PropTypes.string.isRequired,
	uiStore: PropTypes.object.isRequired,
	requireItemById: PropTypes.func.isRequired,
	loadItm: PropTypes.func.isRequired,
	saveItem: PropTypes.func.isRequired
}

export default ProgressModal
